import React from 'react';
import { Button, type ButtonProps } from 'components/button';
import { Image } from 'components/image';
import { Text } from 'components/text';
import styled from '@emotion/styled';
import NoResultsImg from 'img/no-results.png';
import NoDataImg from 'img/no-data.png';
import { Link } from 'react-router-dom';
import { t } from '@lingui/macro';
import { CircularProgress } from '@mui/material';

export type EmptyStateType = 'noResults' | 'noData';

/** `big`: default layout. `small`: compact (e.g. table expanded row, tight panels). */
export type EmptyStateSize = 'big' | 'small';

const EMPTY_STATE_IMAGE_MD = 224;
/** Compact hero — same as theme `spacing.xxxl`×2 + `spacing.md`×2 (160px). Avoid `styledTheme` at module scope (barrel/circular imports in tests). */
const EMPTY_STATE_IMAGE_SM = 160;

export interface EmptyStateAction {
  label: string;
  onClick?: (() => void) | string;
  variant?: ButtonProps['variant'];
  icon?: React.ReactNode;
  buttonProps?: Omit<ButtonProps, 'children' | 'onClick' | 'variant' | 'startIcon'>;
}

export interface EmptyStateProps {
  type: EmptyStateType;
  /** `big` (default) or omit: full layout. `small`: compact image, spacing, and action chrome. */
  size?: EmptyStateSize;
  title?: string;
  description?: string;
  img?: string;
  alt?: string;
  primaryAction?: EmptyStateAction;
  secondaryAction?: EmptyStateAction;
  addRow?: (() => void) | string;
  showAction?: boolean;
  actionProps?: ButtonProps;
  loading?: boolean;
  primaryOnClick?: () => void;
}

/**
 * EmptyState component displays an empty state message with an image, title, description, and optional action buttons.
 * Part of the Delogue design system.
 * @param type - The type of empty state to display ('noResults' or 'noData')
 * @param title - Custom title text (optional, defaults based on type)
 * @param description - Custom description text (optional, defaults based on type)
 * @param img - Custom image source URL or imported image (optional, defaults based on type)
 * @param alt - Custom alt text for the image (optional, defaults based on type)
 * @param primaryAction - Primary action button configuration with label, onClick, variant, icon, and buttonProps
 * @param secondaryAction - Secondary action button configuration with label, onClick, variant, icon, and buttonProps
 * @param addRow - [Deprecated] Legacy prop: callback function or route string for adding a row. Use primaryAction instead.
 * @param showAction - [Deprecated] Legacy prop: whether to show action button. Use primaryAction instead.
 * @param actionProps - [Deprecated] Legacy prop: button props for the action. Use primaryAction instead.
 * @param loading - Whether to show loading spinner instead of the empty state content (default: false)
 * @param size - `big` or omit: default layout. `small`: compact.
 */
const EmptyState = ({
  type,
  size = 'big',
  title,
  description,
  img,
  alt,
  primaryAction,
  secondaryAction,
  actionProps,
  showAction,
  addRow,
  loading = false,
  primaryOnClick,
}: EmptyStateProps) => {
  const content = {
    noResults: {
      img: NoResultsImg,
      alt: t`No results found`,
      title: t`No results found`,
      description: t`We couldn't find any entries matching your search. Try adjusting your search term or clear filters to see all results.`,
    },
    noData: {
      img: NoDataImg,
      alt: t`No data found`,
      title: t`No data exists`,
      description: t`Create your first item.`,
    },
  };

  if (loading) {
    return (
      <Container>
        <Loading>
          <CircularProgress />
        </Loading>
      </Container>
    );
  }

  const legacyPrimaryAction: EmptyStateAction | undefined =
    (showAction || actionProps?.children) && addRow
      ? {
          label: (actionProps?.children as string) || 'Add Item',
          onClick: addRow,
          variant: actionProps?.variant || 'primary',
          icon: actionProps?.startIcon,
          buttonProps: actionProps
            ? (() => {
                const { children, onClick, variant, startIcon, ...rest } = actionProps;
                return rest;
              })()
            : undefined,
        }
      : undefined;

  const effectivePrimaryAction = primaryAction || legacyPrimaryAction;

  const compact = size === 'small';
  const imagePx = compact ? EMPTY_STATE_IMAGE_SM : EMPTY_STATE_IMAGE_MD;

  const renderActionButton = (action: EmptyStateAction, isSecondary = false) => {
    if (!action?.label) return null;
    if (!action.onClick) return null;

    const isLinkAction = typeof action.onClick === 'string';
    const buttonVariant = action.variant || (isSecondary ? 'secondary' : 'primary');
    const { size: actionButtonSize, ...restButtonProps } = action.buttonProps || {};
    const rawSize = actionButtonSize ?? (compact ? 'sm' : 'md');
    const isLinkVariant =
      buttonVariant === 'link' || buttonVariant === 'linkBlack' || buttonVariant === 'linkWhite';
    const buttonSize = !isLinkVariant && rawSize === 'xl' ? 'lg' : rawSize;

    const buttonContent = (
      <Button
        className="actions-button"
        {...({
          variant: buttonVariant,
          size: buttonSize,
          startIcon: action.icon,
          onClick: typeof action.onClick === 'function' ? action.onClick : undefined,
          ...restButtonProps,
          children: action.label,
        } as ButtonProps)}
      />
    );

    if (isLinkAction) {
      return (
        <Link
          to={action.onClick as string}
          onClick={() => (primaryOnClick ? primaryOnClick() : undefined)}
        >
          {buttonContent}
        </Link>
      );
    }

    return buttonContent;
  };

  const hasActions = effectivePrimaryAction || secondaryAction;

  return (
    <Container>
      <Image
        src={img || content[type].img}
        alt={alt || content[type].alt}
        width={imagePx}
        height={imagePx}
      />
      <TitleDescriptionContainer $compact={compact}>
        <Text variant="headlineXs" weight="semiBold">
          {title || content[type].title}
        </Text>
        <Text variant="bodySm" weight="regular" style={{ textAlign: 'center' }} color="tertiary">
          {description || content[type].description}
        </Text>
      </TitleDescriptionContainer>
      {hasActions && (
        <ActionsContainer $compact={compact}>
          {secondaryAction && renderActionButton(secondaryAction, true)}
          {effectivePrimaryAction && renderActionButton(effectivePrimaryAction, false)}
        </ActionsContainer>
      )}
    </Container>
  );
};

const Loading = styled.div`
  height: 40px;
  margin-top: 24px;
`;

const Container = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const TitleDescriptionContainer = styled.div<{ $compact: boolean }>`
  width: ${({ $compact }) => ($compact ? '100%' : '348px')};
  max-width: 348px;
  height: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-top: ${({ $compact }) => ($compact ? '0' : '8px')};
`;

const ActionsContainer = styled.div<{ $compact: boolean }>`
  display: flex;
  flex-direction: row;
  gap: 12px;
  margin-top: ${({ $compact }) => ($compact ? '12px' : '24px')};
  align-items: center;
  justify-content: center;
`;

export default EmptyState;
